export interface CurrentUser {
  tenantId: string;
  actorId: string;
  email: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  twoFactorEnabled: boolean;
  twoFactorPending: boolean;
  /** Only `GET /me` carries it; the login response does not, so the Profile page asks for it. */
  telegram?: { linked: boolean; chatIdSuffix?: string };
  /** Sidebar badge: pending approvals of the tenant. `GET /me` only, and only with `approval.read`. */
  pendingApprovals?: number;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
  ) {
    super(`HTTP ${status}: ${code}`);
  }
}

/**
 * `content-type: application/json` is declared only when there is a body: Fastify refuses a request
 * that announces JSON and sends nothing ("Body cannot be empty when content-type is set to
 * 'application/json'", 400), which is exactly the shape of every body-less POST we make —
 * `/auth/logout`, `/auth/2fa/setup`, `/me/telegram/link-code`.
 */
export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { ...(init?.body === undefined ? {} : { 'content-type': 'application/json' }), ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let code = String(res.status);
    try {
      const body = (await res.json()) as { message?: string | string[] };
      code = Array.isArray(body.message) ? body.message.join('; ') : (body.message ?? code);
    } catch {
      /* no body */
    }
    throw new ApiError(res.status, code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const post = <T,>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

export type LoginResponse = CurrentUser | { twoFactorRequired: true };

export interface TeamMember {
  actorId: string;
  type: 'human' | 'agent';
  displayName: string;
  email: string | null;
  status: string;
  roles: string[];
  businesses: string[];
  twoFactorEnabled: boolean;
  lastLoginAt: string | null;
}
export interface Role { id: string; name: string; kind: string; description: string | null; isSystem: boolean }
export interface Business { id: string; name: string; slug: string; status: string }
export interface PendingInvite { id: string; email: string; expiresAt: string; createdAt: string; role: { name: string } | null }
export interface Credential {
  id: string; provider: string; name: string; scope: string | null; ownerActorId: string | null; ownerBusinessId: string | null;
  metadata: unknown; createdAt: string; rotatedAt: string | null; revokedAt: string | null;
}
export interface PackageHistoryRow { id: string; name: string; direction: 'export' | 'import'; formatVersion: number; checksum: string; createdAt: string }
export interface ImportSummary {
  roleTemplates: { created: number; updated: number; skipped: number };
  skills: { created: number; updated: number; skipped: number };
  recipes: { created: number; updated: number; skipped: number };
}

/** Core endpoints. Feature blocks add their own `apps/web/src/api/<block>.ts` using request/post. */
export const api = {
  health: () => request<{ status: string; db: boolean }>('/health'),
  me: () => request<CurrentUser>('/me'),
  login: (email: string, password: string) => post<LoginResponse>('/auth/login', { email, password }),
  verify2fa: (code: string) => post<CurrentUser>('/auth/2fa/verify', { code }),
  setup2fa: () => post<{ secret: string; otpauth: string }>('/auth/2fa/setup'),
  enable2fa: (code: string) => post<{ recoveryCodes: string[] }>('/auth/2fa/enable', { code }),
  disable2fa: (code: string) => post<{ ok: true }>('/auth/2fa/disable', { code }),
  logout: () => post<{ ok: true }>('/auth/logout'),
  inviteInfo: (token: string) => request<{ email: string; role: string | null; expiresAt: string }>(`/auth/invites/${encodeURIComponent(token)}`),
  acceptInvite: (token: string, password: string, displayName: string) => post<{ ok: true }>('/auth/invites/accept', { token, password, displayName }),
  createInvite: (email: string, role: string, businessIds: string[]) => post<{ inviteUrl: string; expiresAt: string }>('/auth/invites', { email, role, businessIds }),

  teamMembers: () => request<TeamMember[]>('/team/members'),
  teamRoles: () => request<Role[]>('/team/roles'),
  teamBusinesses: () => request<Business[]>('/team/businesses'),
  teamInvites: () => request<PendingInvite[]>('/team/invites'),

  credentials: () => request<Credential[]>('/credentials'),
  credentialProviders: () => request<string[]>('/credentials/providers'),
  createCredential: (input: { provider: string; name: string; secret: string; scope?: string }) => post<Credential>('/credentials', input),
  rotateCredential: (id: string, secret: string) => post<Credential>(`/credentials/${id}/rotate`, { secret }),
  revokeCredential: (id: string) => request<Credential>(`/credentials/${id}`, { method: 'DELETE' }),

  packageHistory: () => request<PackageHistoryRow[]>('/content-packages'),
  exportPackage: (name: string) => request<Record<string, unknown>>(`/content-packages/export?name=${encodeURIComponent(name)}`),
  importPackage: (pkg: unknown) => post<ImportSummary>('/content-packages/import', pkg),
};
